import React, { useState } from 'react';
import { Card, Row, Col, Button, Tag, Icon } from 'antd';
import { Chart, Geom, Axis, Coord, Legend, Label, Tooltip as ChartTooltip } from 'bizcharts';
import DataSet from '@antv/data-set';
import { StyledCard, SectionTitle } from './QualityDashboard.styles';

// Av. Review Score by Category (Hotel Link: 6 categories, score color green ≥4.7, yellow 4.6–4.69, red <4.6)
const categoryScoreData = [
  {
    key: 'cleanliness',
    label: 'Cleanliness',
    icon: 'skin',
    description: 'How clean and tidy the space is upon guest arrival.',
    score: 4.6,
    positiveTags: [{ tag: 'Extremely clean and tidy', pct: 28 }, { tag: 'Fresh towels', pct: 17 }, { tag: 'Spotless sheets', pct: 12 }],
    negativeTags: [{ tag: 'Dirty bathroom', pct: 22 }, { tag: 'Stained linens', pct: 9 }, { tag: 'Dusty surfaces', pct: 7 }]
  },
  {
    key: 'accuracy',
    label: 'Accuracy',
    icon: 'check-circle',
    description: 'How closely your listing description matches the actual space.',
    score: 4.7,
    positiveTags: [{ tag: 'Looked like the photos', pct: 30 }, { tag: 'Matched the description', pct: 25 }],
    negativeTags: [{ tag: 'Needs maintenance', pct: 7 }]
  },
  {
    key: 'communication',
    label: 'Communication',
    icon: 'message',
    description: 'How promptly and helpfully you respond to guests.',
    score: 4.8,
    positiveTags: [{ tag: 'Quick responses', pct: 35 }, { tag: 'Helpful host', pct: 22 }],
    negativeTags: [{ tag: 'Slow to respond', pct: 10 }]
  },
  {
    key: 'location',
    label: 'Location',
    icon: 'environment',
    description: 'How guests feel about the area and convenience of the location.',
    score: 4.9,
    positiveTags: [{ tag: 'Great location', pct: 40 }, { tag: 'Easy to get around', pct: 18 }],
    negativeTags: [{ tag: 'Noisy area', pct: 5 }]
  },
  {
    key: 'checkin',
    label: 'Check-in',
    icon: 'key',
    description: 'How smooth and easy the check-in process is.',
    score: 4.5,
    positiveTags: [{ tag: 'Clear instructions', pct: 32 }, { tag: 'Flexible check-in', pct: 20 }],
    negativeTags: [{ tag: 'Unclear instructions', pct: 25 }]
  },
  {
    key: 'value',
    label: 'Value',
    icon: 'tag',
    description: 'How guests rate the overall value for the price paid.',
    score: 4.4,
    positiveTags: [{ tag: 'Good value', pct: 24 }],
    negativeTags: [{ tag: 'Overpriced', pct: 20 }]
  }
];

function getScoreColor(score) {
  if (score >= 4.7) return '#52c41a';
  if (score >= 4.6) return '#faad14';
  return '#ff4d4f';
}

function getFiveStarColor(pct) {
  if (pct >= 40) return '#52c41a';
  if (pct >= 30) return '#faad14';
  return '#ff4d4f';
}

// Host overview: listing status (same structure as Demo 4 accommodationStatuses – label, count, color)
const listingStatusData = [
  { label: 'Good', count: 2, color: '#52c41a' },
  { label: 'Educate', count: 5, color: '#faad14' },
  { label: 'Warn', count: 2, color: '#fa8c16' },
  { label: 'Probation', count: 1, color: '#fa541c' },
  { label: 'Additional warn', count: 0, color: '#fa8c16' },
  { label: 'Pending removal', count: 0, color: '#ff4d4f' },
  { label: 'Suspend', count: 0, color: '#ff4d4f' },
  { label: 'Removed', count: 0, color: '#ff4d4f' }
];

const top3NegativeTags = [
  { category: 'Cleanliness', tag: 'Dirty bathroom', pct: 30 },
  { category: 'Check-in', tag: 'Unclear instructions', pct: 25 },
  { category: 'Communication', tag: 'Slow to respond', pct: 15 }
];

const QualityDashboardDemo5 = () => {
  const [expandedCategoryKey, setExpandedCategoryKey] = useState(null);

  const currentOverallScore = 4.75;
  const fiveStarRatePct = 64.2;
  const reviewCount = 187;
  const listingCount = 11;

  // Filter count > 0 so chart percentages are valid (avoid NaN)
  const listingStatusPieData = listingStatusData.filter(s => s.count > 0).map(s => ({ type: s.label, count: s.count, color: s.color }));
  const { DataView } = DataSet;
  const listingDv = new DataView();
  listingDv.source(listingStatusPieData).transform({
    type: 'percent',
    field: 'count',
    dimension: 'type',
    as: 'percent'
  });
  const listingCols = {
    percent: { formatter: val => (Number(val) * 100).toFixed(2) + '%' }
  };

  return (
    <StyledCard
      title={
        <span>
          Airbnb Quality Dashboard (Demo 5 — Hotel Link style){' '}
          <a
            href="https://blog.hotellinksolutions.com/airbnb-quality-dashboard-monitor-key-metrics-at-a-glance-with-hotel-link"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}
          >
            Reference Link - Hotel Link Blog
          </a>
        </span>
      }
    >
      {/* Av. Review Score by Category */}
      <SectionTitle style={{ marginBottom: 16 }}>Av. Review Score by Category</SectionTitle>
      <p style={{ marginBottom: 16, fontSize: 13, color: '#595959' }}>
        Average ratings from guest reviews across each category. Green (4.7+): Good. Yellow (4.6–4.69): Below recommended. Red (&lt;4.6): Needs
        attention.
      </p>
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {categoryScoreData.map(cat => {
          const color = getScoreColor(cat.score);
          const showTips = cat.score < 4.7;
          return (
            <Col xs={24} sm={12} md={8} key={cat.key}>
              <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Icon type={cat.icon} style={{ fontSize: 20, color: '#595959' }} />
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{cat.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 22, fontWeight: 600, color }}>{cat.score}</span>
                    {showTips && <Icon type="warning" style={{ color, fontSize: 16 }} />}
                  </div>
                </div>
                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 12, paddingTop: 8 }}>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, color: '#1890ff', height: 'auto' }}
                    onClick={() => setExpandedCategoryKey(expandedCategoryKey === cat.key ? null : cat.key)}
                  >
                    Learn more
                    <Icon type={expandedCategoryKey === cat.key ? 'up' : 'down'} style={{ marginLeft: 4, fontSize: 12 }} />
                  </Button>
                </div>
                {expandedCategoryKey === cat.key && (
                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        background: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: 4,
                        padding: 12,
                        marginBottom: 12,
                        fontSize: 12,
                        color: '#595959'
                      }}
                    >
                      <Icon type="warning" style={{ color: '#faad14', marginRight: 8 }} />
                      To be a Superhost, you need to maintain an overall rating of at least 4.8 or higher by addressing the negative tags from guests
                      listed below.
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>Negative tags:</strong>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      {cat.negativeTags.map((n, i) => (
                        <Tag key={i} color="red" style={{ marginBottom: 4 }}>
                          {n.tag} ({n.pct}%)
                        </Tag>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>Positive tags:</strong>
                    </div>
                    <div>
                      {cat.positiveTags.map((p, i) => (
                        <Tag key={i} color="green" style={{ marginBottom: 4 }}>
                          {p.tag} ({p.pct}%)
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Host overview – left: 2 even boxes + tags (smaller); right: chart (biggest) */}
      <SectionTitle style={{ marginBottom: 16 }}>Host overview</SectionTitle>
      <p style={{ fontSize: 13, color: '#595959', marginBottom: 16 }}>This section provides a snapshot of your hosting performance.</p>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={10}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', height: 160 }}>
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>Overall av. review score</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Icon type="trophy" style={{ fontSize: 20, color: '#52c41a' }} />
                    <span style={{ fontSize: 36, fontWeight: 600, color: getScoreColor(currentOverallScore) }}>{currentOverallScore}</span>
                    <Icon type="trophy" style={{ fontSize: 20, color: '#52c41a' }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>(based on {reviewCount} reviews)</div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', height: 160 }}>
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>5-star review rate</div>
                  <div style={{ fontSize: 36, fontWeight: 600, color: getFiveStarColor(fiveStarRatePct) }}>{fiveStarRatePct}%</div>
                  <div style={{ marginTop: 4 }}>
                    {[1, 2, 3, 4, 5].map(i => (
                      <Icon key={i} type="star" theme="filled" style={{ color: '#faad14', marginRight: 2 }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>(based on {reviewCount} reviews)</div>
                </div>
              </Card>
            </Col>
            <Col xs={24}>
              <Card
                size="small"
                title="Top 3 negative tags"
                style={{ borderRadius: 8, border: '1px solid #e8e8e8', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}
              >
                <p style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                  The most frequently mentioned concerns in guest feedback, helping you focus on what matters most to guests.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {top3NegativeTags.map((t, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'block',
                        border: '1px solid #ff4d4f',
                        borderRadius: 8,
                        padding: '12px 16px',
                        background: '#fff',
                        color: '#262626',
                        fontSize: 14
                      }}
                    >
                      {t.category} – {t.tag} ({t.pct}% of total reviews)
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xs={24} lg={14}>
          <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', height: '100%' }}>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>Listing Status (based on {listingCount} listings)</div>
            <p style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>A chart showing the number of listings and their current status.</p>
            <div style={{ minHeight: 340, position: 'relative' }}>
              {listingStatusPieData.length > 0 ? (
                <Chart data={listingDv} scale={listingCols} forceFit height={340}>
                  <Coord type="theta" radius={0.85} innerRadius={0.6} />
                  <Axis name="percent" />
                  <Geom
                    type="intervalStack"
                    position="percent"
                    color={[
                      'type',
                      type => {
                        const m = listingStatusPieData.find(d => d.type === type);
                        return (m && m.color) || '#999';
                      }
                    ]}
                    style={{ lineWidth: 1, stroke: '#fff' }}
                    tooltip={[
                      'type*percent',
                      (type, percent) => {
                        const m = listingStatusPieData.find(d => d.type === type);
                        return { name: type, value: m && m.count != null ? m.count : 0 };
                      }
                    ]}
                  >
                    <Label content="percent" formatter={(val, item) => item.point.type} />
                    <Legend position="right" itemFormatter={val => val} marker="circle" />
                  </Geom>
                  <ChartTooltip showTitle={false} />
                </Chart>
              ) : (
                <div style={{ width: '100%', textAlign: 'center', color: '#8c8c8c', paddingTop: 80 }}>No listing data</div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </StyledCard>
  );
};

export default QualityDashboardDemo5;
